import React, { Suspense, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { devToolsEnabled } from 'constants/devtools';
import { ROLLOUT_AUTOJOIN_SIDEBAR } from 'constants/featureFlag';
import { INVITE_CALLOUT_DISMISSED } from 'constants/localStorage';
import { ORG_ROLE_VIEWER } from 'constants/organizationRoles';
import {
  INVITE_ADMIN_ACTION,
  INVITE_CREATOR_ACTION,
  INVITE_CREATOR_LITE_ACTION,
  INVITE_VIEWER_ACTION,
} from 'constants/scopes';
import { WORKSPACE_DOMAIN_JOIN_INFO } from 'constants/settings';
import { SHOW_GET_STARTED_CHECKLIST } from 'constants/triggers';
import { REQUEST_STATUSES } from 'constants/workspaceJoinRequests';
import { completeTriggerCreator } from 'creators/triggers';
import { selectNavigationScopes } from 'selectors/navigation';
import { selectCurrentUser } from 'selectors/user';
import { platformDetails } from 'utilities/device';
import { useLocalWorkspaceState } from 'utilities/local-workspace-state';
import {
  useAutojoinBannerState,
  useAutojoinTimedDismissalState,
  useSuggestedWorkspaceBannerState,
  useSuggestedWorkspaceTimedDismissalState,
} from 'utilities/localStorage/suggestedWorkspace';
import { useTimedDismissal } from 'utilities/localStorage/utils';

import { useFlagIsActivated } from 'hooks/featureFlag';
import { useShouldShowTrigger } from 'hooks/triggers';
import { useHasSomeScopes } from 'hooks/useHasScopes';
import { useMatchMobileOnly } from 'hooks/useMatchMedia';
import { useGetSelectedWorkspace } from 'hooks/workspace';
import { useWorkspaceSetting } from 'hooks/workspaceSettings';

import { DevToolCallout } from '../devtool-callout';

import { MAX_ACCOUNT_AGE } from 'components/layout/navigation/get-started-checklist';
import useSuggestedWorkspace from 'components/layout/navigation/useSuggestedWorkspace';

const JoinTeamSidebarCallout = React.lazy(() =>
  import('../JoinTeamSidebarCallout')
);
const AutojoinCallout = React.lazy(() => import('../AutoJoinCallout'));

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const InviteCallout = React.lazy(() => import('../invite-callout'));
const GetStartedChecklist = React.lazy(() =>
  import('../get-started-checklist')
);

export const CALLOUTS = {
  INVITE_CALLOUT: 'INVITE_CALLOUT',
  GET_STARTED_CHECKLIST_CALLOUT: 'GET_STARTED_CHECKLIST_CALLOUT',
  JOIN_TEAM_CALLOUT: 'JOIN_TEAM_CALLOUT',
  AUTO_JOIN_CALLOUT: 'AUTO_JOIN_CALLOUT',
};

export function SidebarCallout({ displayMode }) {
  const suggestedWorkspace = useSuggestedWorkspace();
  const callout = useSidebarCallout();

  const suggestion = suggestedWorkspace?.result?.data?.result;
  const isSuggestionReady =
    suggestedWorkspace?.result?.called && !suggestedWorkspace?.result?.loading;

  switch (callout) {
    case CALLOUTS.INVITE_CALLOUT:
      return (
        <Suspense fallback={null}>
          <InviteCallout displayMode={displayMode} />
        </Suspense>
      );
    case CALLOUTS.GET_STARTED_CHECKLIST_CALLOUT:
      return (
        <Suspense fallback={null}>
          <GetStartedChecklist displayMode={displayMode} />
        </Suspense>
      );
    case CALLOUTS.JOIN_TEAM_CALLOUT: {
      if (devToolsEnabled) {
        if (!isSuggestionReady) {
          return null;
        }

        if (!suggestion) {
          return (
            <DevToolCallout displayMode={displayMode}>
              Cannot display join team callout because there is no suggested
              workspace
            </DevToolCallout>
          );
        }
      }

      return (
        <Suspense fallback={null}>
          <JoinTeamSidebarCallout
            displayMode={displayMode}
            requestPending={
              suggestion.requestStatus === REQUEST_STATUSES.PENDING
            }
            autoJoin={suggestion.autoJoin}
            workspace={suggestion.workspace}
          />
        </Suspense>
      );
    }
    case CALLOUTS.AUTO_JOIN_CALLOUT: {
      if (devToolsEnabled) {
        if (!isSuggestionReady) {
          return null;
        }

        if (!suggestion) {
          return (
            <DevToolCallout displayMode={displayMode}>
              Cannot display autojoin callout because there is no suggested
              workspace
            </DevToolCallout>
          );
        }
      }

      return (
        <Suspense fallback={null}>
          <AutojoinCallout
            displayMode={displayMode}
            workspace={suggestion.workspace}
          />
        </Suspense>
      );
    }
    default:
      return null;
  }
}

const urlParams = new URLSearchParams(window.location.search);

function shouldShowJoinTeamBanner(suggestion) {
  return (
    suggestion?.workspace &&
    !suggestion.hasPendingInvitation &&
    !suggestion.isCurrentUserMember &&
    suggestion.requestStatus !== REQUEST_STATUSES.APPROVED
  );
}

function useShouldJoinTeamBannerBeTimeDismissed() {
  const [manualDismissed] = useSuggestedWorkspaceBannerState();

  const [timeDismissal, setTimedDismissal] =
    useSuggestedWorkspaceTimedDismissalState();

  return useTimedDismissal({
    manualDismissed,
    timeDismissal,
    setTimedDismissal,
  });
}

function useHasAutojoin() {
  const { value: domainSettings = {} } = useWorkspaceSetting(
    WORKSPACE_DOMAIN_JOIN_INFO
  );
  const hasAutoJoin = useMemo(
    () => Object.values(domainSettings).some(({ autoJoin }) => autoJoin),
    [domainSettings]
  );

  return hasAutoJoin;
}

function useShouldAutojoinBannerBeTimeDismissed() {
  const [manualDismissed] = useAutojoinBannerState();
  const [timeDismissal, setTimedDismissal] = useAutojoinTimedDismissalState();

  return useTimedDismissal({
    manualDismissed,
    timeDismissal,
    setTimedDismissal,
  });
}

function shouldShowAutojoinCallout(suggestion, currentWorkspace) {
  return (
    suggestion?.workspace &&
    suggestion.workspace.id === currentWorkspace?.id &&
    suggestion.isCurrentUserMember
  );
}

export function useSidebarCallout() {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const workspace = useGetSelectedWorkspace();
  const isMobileScreenSize = useMatchMobileOnly();
  const suggestedWorkspace = useSuggestedWorkspace();
  const suggestedWorkspaceDismissed = useShouldJoinTeamBannerBeTimeDismissed();
  const autojoinDismissed = useShouldAutojoinBannerBeTimeDismissed();
  const selectedWorkspace = useGetSelectedWorkspace();
  const hasInviteCalloutScope = useHasSomeScopes([
    INVITE_VIEWER_ACTION,
    INVITE_CREATOR_ACTION,
    INVITE_ADMIN_ACTION,
    INVITE_CREATOR_LITE_ACTION,
  ]);
  const scopes = useSelector(selectNavigationScopes);
  const hasAutoJoin = useHasAutojoin();

  const hasAutoJoinFeatureFlag = useFlagIsActivated({
    flag: ROLLOUT_AUTOJOIN_SIDEBAR,
    activationValues: ['show-new'],
  });

  const shouldShowGetStartedChecklist = useShouldShowTrigger(
    SHOW_GET_STARTED_CHECKLIST
  );
  const [inviteCalloutDisabled] = useLocalWorkspaceState(
    INVITE_CALLOUT_DISMISSED,
    false
  );

  const hasSuggestedWorkspaces = suggestedWorkspace.hasFlag;
  const suggestedWorkspaceResult = suggestedWorkspace.result;
  const suggestion = suggestedWorkspace?.result?.data?.result;

  const canShowInviteCallout = !inviteCalloutDisabled && hasInviteCalloutScope;

  if (devToolsEnabled) {
    const param = urlParams.get('sidebar-callout');

    if (param && CALLOUTS[param]) {
      return param;
    }
  }

  if (shouldShowGetStartedChecklist) {
    const isViewer = workspace?.memberRole === ORG_ROLE_VIEWER;
    const createdAt = new Date(user.createdAt);
    const currentDate = new Date();

    const daysSinceAccountCreation = Math.floor(
      (currentDate - createdAt) / ONE_DAY_MS
    );

    if (daysSinceAccountCreation > MAX_ACCOUNT_AGE) {
      dispatch(completeTriggerCreator(SHOW_GET_STARTED_CHECKLIST));
    } else if (isViewer) {
      return null;
    } else if (platformDetails.type === 'desktop') {
      return isMobileScreenSize ? null : CALLOUTS.GET_STARTED_CHECKLIST_CALLOUT;
    }
  }

  if (hasSuggestedWorkspaces || hasAutoJoinFeatureFlag) {
    if (
      hasSuggestedWorkspaces &&
      shouldShowJoinTeamBanner(suggestion) &&
      !suggestedWorkspaceDismissed &&
      scopes.hasSuggestedWorkspaceSidebarScope
    ) {
      return CALLOUTS.JOIN_TEAM_CALLOUT;
    }

    if (suggestedWorkspaceResult && !suggestedWorkspaceResult.loading) {
      const hasInviteScope =
        scopes.hasInviteViewerActionScope || scopes.hasInviteCreatorActionScope;

      if (
        hasAutoJoinFeatureFlag &&
        scopes.hasWorkspaceJoinManageScope &&
        scopes.hasAutojoinSidebarScope &&
        hasInviteScope &&
        !hasAutoJoin &&
        shouldShowAutojoinCallout(suggestion, selectedWorkspace) &&
        !autojoinDismissed
      ) {
        return CALLOUTS.AUTO_JOIN_CALLOUT;
      }

      return canShowInviteCallout ? CALLOUTS.INVITE_CALLOUT : null;
    }
  }

  // These values will be nullish until the suggested workspace check completes
  if (
    hasSuggestedWorkspaces == undefined ||
    hasAutoJoinFeatureFlag == undefined
  ) {
    return null;
  }

  return canShowInviteCallout ? CALLOUTS.INVITE_CALLOUT : null;
}
